import React, { useMemo, useState } from 'react';
import { FileText, Download, ExternalLink, Star, Clock, CheckCircle2, AlertTriangle, Loader2, BookOpen, Search, Sparkles, ArrowUpDown, X, Send, ChevronDown } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { useLanguage } from '../../i18n/LanguageContext';
interface ReportsTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
interface Report {
  id: string;
  title: string;
  type: 'natal' | 'solar' | 'compatibility' | 'forecast' | 'combo';
  date: string;
  dateSort: number;
  status: 'generating' | 'ready' | 'error';
  pages: number;
  reviewed: boolean;
  rating: number | null;
  orderId: string;
  progress?: number;
}
const typeConfig: Record<string, {
  label: string;
  icon: string;
  bg: string;
  text: string;
}> = {
  natal: {
    label: 'Натальная карта',
    icon: '🌟',
    bg: 'bg-indigo-50',
    text: 'text-indigo-600'
  },
  solar: {
    label: 'Соляр',
    icon: '☀️',
    bg: 'bg-amber-50',
    text: 'text-amber-600'
  },
  compatibility: {
    label: 'Совместимость',
    icon: '💞',
    bg: 'bg-rose-50',
    text: 'text-rose-600'
  },
  forecast: {
    label: 'Прогноз',
    icon: '🔮',
    bg: 'bg-teal-50',
    text: 'text-teal-600'
  },
  combo: {
    label: 'Комбо-анализ',
    icon: '✨',
    bg: 'bg-purple-50',
    text: 'text-purple-600'
  }
};
const REPORTS_PER_PAGE = 6;
export function ReportsTab({
  theme,
  onNavigate
}: ReportsTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'new' | 'old'>('new');
  const [visibleCount, setVisibleCount] = useState(REPORTS_PER_PAGE);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submittedReviews, setSubmittedReviews] = useState<string[]>([]);
  const reports: Report[] = [{
    id: 'RPT-001',
    title: 'Натальная карта (Анна)',
    type: 'natal',
    date: '12 Окт 2024',
    dateSort: 20241012,
    status: 'ready',
    pages: 42,
    reviewed: false,
    rating: null,
    orderId: 'ORD-1201'
  }, {
    id: 'RPT-002',
    title: 'Соляр 2024 (Анна)',
    type: 'solar',
    date: '10 Окт 2024',
    dateSort: 20241010,
    status: 'ready',
    pages: 36,
    reviewed: true,
    rating: 5,
    orderId: 'ORD-1198'
  }, {
    id: 'RPT-003',
    title: 'Совместимость (Анна + Иван)',
    type: 'compatibility',
    date: '15 Сен 2024',
    dateSort: 20240915,
    status: 'ready',
    pages: 58,
    reviewed: false,
    rating: null,
    orderId: 'ORD-1185'
  }, {
    id: 'RPT-004',
    title: 'Прогноз на Ноябрь 2024',
    type: 'forecast',
    date: '01 Ноя 2024',
    dateSort: 20241101,
    status: 'generating',
    pages: 0,
    reviewed: false,
    rating: null,
    orderId: 'ORD-1220',
    progress: 68
  }, {
    id: 'RPT-005',
    title: 'Комбо-анализ (Анна)',
    type: 'combo',
    date: '20 Авг 2024',
    dateSort: 20240820,
    status: 'ready',
    pages: 62,
    reviewed: true,
    rating: 4,
    orderId: 'ORD-1150'
  }];
  const filtered = useMemo(() => {
    let result = reports;
    if (filterType !== 'all') result = result.filter(r => r.type === filterType);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => r.title.toLowerCase().includes(q) || r.id.toLowerCase().includes(q) || typeConfig[r.type].label.toLowerCase().includes(q));
    }
    result.sort((a, b) => sortOrder === 'new' ? b.dateSort - a.dateSort : a.dateSort - b.dateSort);
    return result;
  }, [filterType, searchQuery, sortOrder]);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;
  const readyCount = reports.filter(r => r.status === 'ready').length;
  const totalPages = reports.filter(r => r.status === 'ready').reduce((s, r) => s + r.pages, 0);
  const unreviewed = reports.filter(r => r.status === 'ready' && !r.reviewed && !submittedReviews.includes(r.id));
  const handleSubmitReview = (reportId: string) => {
    if (reviewRating === 0) return;
    setSubmittedReviews(prev => [...prev, reportId]);
    setReviewingId(null);
    setReviewRating(0);
    setReviewText('');
    showToast(t('userdash.reports.reviewThanks'));
  };
  return <div className="space-y-5" data-id="element-20274">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-20275">
        <div data-id="element-20276">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-20277">
            {t('userdash.reports.title')}
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5" data-id="element-20278">
            {t('userdash.reports.readyCount', {
            count: readyCount.toString()
          })}{' '}
            ·{' '}
            {t('userdash.reports.pagesCount', {
            count: totalPages.toString()
          })}
          </p>
        </div>
        <Button size="sm" onClick={() => onNavigate('home')} data-id="element-20279">
          <Sparkles className="w-4 h-4 mr-1.5" data-id="element-20280" />{' '}
          {t('userdash.reports.orderNew')}
        </Button>
      </div>

      {/* Review Strip */}
      {unreviewed.length > 0 && <div className="flex items-center gap-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-20281">
          <Star className="w-4 h-4 text-amber-500 shrink-0" data-id="element-20282" />
          <span className="text-xs text-amber-800 flex-1" data-id="element-20283">
            <span className="font-medium" data-id="element-20284">
              {t('userdash.reports.rateReport')}
            </span>{' '}
            — {t('userdash.reports.getDiscount')}
          </span>
          <button onClick={() => {
        setReviewingId(unreviewed[0].id);
        setReviewRating(0);
        setReviewText('');
      }} className="text-xs font-medium text-amber-700 hover:text-amber-900 underline shrink-0" data-id="element-20285">
            {t('userdash.reports.rate')}
          </button>
        </div>}

      {/* Search + Filters + Sort */}
      <div className="space-y-3" data-id="element-20286">
        <div className="relative" data-id="element-20287">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-20288" />
          <input type="text" placeholder={t('userdash.reports.search')} value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setVisibleCount(REPORTS_PER_PAGE);
        }} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white" data-id="element-20289" />
        </div>
        <div className="flex items-center justify-between gap-2" data-id="element-20290">
          <div className="flex items-center gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-0.5" data-id="element-20291">
            <button onClick={() => {
            setFilterType('all');
            setVisibleCount(REPORTS_PER_PAGE);
          }} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors shrink-0 ${filterType === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-20292">
              {t('userdash.reports.all')} ({reports.length})
            </button>
            {Object.entries(typeConfig).map(([key, cfg]) => {
            const count = reports.filter(r => r.type === key).length;
            if (count === 0) return null;
            return <button key={key} onClick={() => {
              setFilterType(key);
              setVisibleCount(REPORTS_PER_PAGE);
            }} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors shrink-0 ${filterType === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-20293">
                  {cfg.icon} {cfg.label} ({count})
                </button>;
          })}
          </div>
          <button onClick={() => setSortOrder(sortOrder === 'new' ? 'old' : 'new')} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors shrink-0" data-id="element-20294">
            <ArrowUpDown className="w-3 h-3" data-id="element-20295" />
            {sortOrder === 'new' ? t('userdash.reports.sortNew') : t('userdash.reports.sortOld')}
          </button>
        </div>
      </div>

      {/* Reports List */}
      {visible.length > 0 ? <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-20296">
          {visible.map(report => {
        const tc = typeConfig[report.type];
        const wasReviewed = report.reviewed || submittedReviews.includes(report.id);
        const isReviewing = reviewingId === report.id;
        return <div key={report.id} data-id="element-20297">
                <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/50 transition-colors" data-id="element-20298">
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-lg ${tc.bg} flex items-center justify-center text-base shrink-0`} data-id="element-20299">
                    {tc.icon}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0" data-id="element-20300">
                    <div className="font-semibold text-sm text-slate-900 truncate" data-id="element-20301">
                      {report.title}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap" data-id="element-20302">
                      <span className="text-[11px] text-slate-500 flex items-center gap-0.5" data-id="element-20303">
                        <Clock className="w-3 h-3" data-id="element-20304" /> {report.date}
                      </span>
                      {report.status === 'ready' && report.pages > 0 && <span className="text-[11px] text-slate-500 flex items-center gap-0.5" data-id="element-20305">
                          <BookOpen className="w-3 h-3" data-id="element-20306" /> {report.pages}{' '}
                          {t('userdash.reports.pagesShort')}
                        </span>}
                      {wasReviewed && <span className="text-[11px] text-amber-600 flex items-center gap-0.5" data-id="element-20307">
                          <Star className="w-3 h-3 fill-amber-400" data-id="element-20308" />{' '}
                          {report.rating || 5}/5
                        </span>}
                      <span className="text-[10px] text-slate-400 hidden sm:inline" data-id="element-20309">
                        {report.orderId}
                      </span>
                    </div>
                  </div>

                  {/* Status + Actions */}
                  <div className="flex items-center gap-2 shrink-0" data-id="element-20310">
                    {report.status === 'ready' && <>
                        {!wasReviewed && !isReviewing && <button onClick={() => {
                  setReviewingId(report.id);
                  setReviewRating(0);
                  setReviewText('');
                }} className="p-1.5 text-slate-300 hover:text-amber-500 transition-colors hidden sm:block" title={t('userdash.reports.rateReportTitle')} data-id="element-20311">
                            <Star className="w-4 h-4" data-id="element-20312" />
                          </button>}
                        <button onClick={() => showToast(t('userdash.reports.downloadingPdf'))} className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors hidden sm:block" title={t('userdash.reports.downloadPdf')} data-id="element-20313">
                          <Download className="w-4 h-4" data-id="element-20314" />
                        </button>
                        <Button variant="secondary" size="sm" className="text-xs px-3 h-7" onClick={() => onNavigate('full-report')} data-id="element-20315">
                          <ExternalLink className="w-3.5 h-3.5 mr-1" data-id="element-20316" />{' '}
                          {t('userdash.reports.open')}
                        </Button>
                      </>}

                    {report.status === 'generating' && <div className="flex items-center gap-2" data-id="element-20317">
                        <div className="hidden sm:flex items-center gap-2 w-24" data-id="element-20318">
                          <div className="flex-1 bg-slate-100 rounded-full h-1.5" data-id="element-20319">
                            <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{
                      width: `${report.progress || 0}%`
                    }} data-id="element-20320" />
                          </div>
                          <span className="text-[10px] text-slate-500 shrink-0" data-id="element-20321">
                            {report.progress}%
                          </span>
                        </div>
                        <Badge variant="warning" size="sm" className="text-[10px] py-0 h-5 px-2 flex items-center gap-1" data-id="element-20322">
                          <Loader2 className="w-3 h-3 animate-spin" data-id="element-20323" />{' '}
                          {t('userdash.reports.generating')}
                        </Badge>
                      </div>}

                    {report.status === 'error' && <Button variant="outline" size="sm" className="text-xs px-3 h-7 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => showToast(t('userdash.reports.retryStarted'))} data-id="element-20324">
                        <AlertTriangle className="w-3.5 h-3.5 mr-1" data-id="element-20325" />{' '}
                        {t('userdash.reports.retry')}
                      </Button>}
                  </div>
                </div>

                {/* Mobile actions row */}
                {report.status === 'ready' && <div className="flex items-center gap-2 px-4 pb-3 sm:hidden" data-id="element-20326">
                    <Button variant="secondary" size="sm" className="flex-1 text-xs h-8" onClick={() => onNavigate('full-report')} data-id="element-20327">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" data-id="element-20328" />{' '}
                      {t('userdash.reports.open')}
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={() => showToast(t('userdash.reports.downloadingPdf'))} data-id="element-20329">
                      <Download className="w-3.5 h-3.5 mr-1" data-id="element-20330" />{' '}
                      {t('userdash.reports.download')}
                    </Button>
                    {!wasReviewed && !isReviewing && <button onClick={() => {
              setReviewingId(report.id);
              setReviewRating(0);
              setReviewText('');
            }} className="p-2 text-slate-400 hover:text-amber-500 transition-colors" title={t('userdash.reports.rate')} data-id="element-20331">
                        <Star className="w-4 h-4" data-id="element-20332" />
                      </button>}
                  </div>}

                {/* Inline Review */}
                {isReviewing && <div className="px-4 pb-3 pt-1 bg-slate-50/50 space-y-2" data-id="element-20333">
                    <div className="flex items-center justify-between" data-id="element-20334">
                      <span className="text-xs font-medium text-slate-700" data-id="element-20335">
                        {t('userdash.reports.rateReportTitle')}
                      </span>
                      <button onClick={() => setReviewingId(null)} className="p-1 text-slate-400 hover:text-slate-600" data-id="element-20336">
                        <X className="w-3.5 h-3.5" data-id="element-20337" />
                      </button>
                    </div>
                    <div className="flex gap-0.5" data-id="element-20338">
                      {[1, 2, 3, 4, 5].map(s => <button key={s} onMouseEnter={() => setReviewHover(s)} onMouseLeave={() => setReviewHover(0)} onClick={() => setReviewRating(s)} className="p-0.5" data-id="element-20339">
                          <Star className={`w-5 h-5 transition-colors ${s <= (reviewHover || reviewRating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-20340" />
                        </button>)}
                    </div>
                    {reviewRating > 0 && <div className="flex gap-2" data-id="element-20341">
                        <input type="text" value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder={reviewRating >= 4 ? t('userdash.reports.whatDidYouLike') : t('userdash.reports.whatToImprove')} className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-500" data-id="element-20342" />
                        <Button size="sm" className="text-xs h-7 px-3" onClick={() => handleSubmitReview(report.id)} data-id="element-20343">
                          <Send className="w-3 h-3 mr-1" data-id="element-20344" />{' '}
                          {t('userdash.reports.send')}
                        </Button>
                      </div>}
                  </div>}

                {/* Review confirmation */}
                {submittedReviews.includes(report.id) && !report.reviewed && <div className="px-4 pb-3" data-id="element-20345">
                    <div className="flex items-center gap-2 text-[11px] text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg" data-id="element-20346">
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" data-id="element-20347" />
                      {t('userdash.reports.discountActivated')}
                    </div>
                  </div>}
              </div>;
      })}
        </div> : <div className="text-center py-12" data-id="element-20348">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3" data-id="element-20349">
            <FileText className="w-7 h-7 text-slate-300" data-id="element-20350" />
          </div>
          <h3 className="font-bold text-slate-900 mb-1 text-sm" data-id="element-20351">
            {searchQuery ? t('userdash.reports.nothingFound') : t('userdash.reports.noReports')}
          </h3>
          <p className="text-xs text-slate-500 mb-4" data-id="element-20352">
            {searchQuery ? t('userdash.reports.tryChangingQuery') : t('userdash.reports.orderFirstReport')}
          </p>
          {!searchQuery && <Button size="sm" onClick={() => onNavigate('home')} data-id="element-20353">
              {t('userdash.reports.orderReport')}
            </Button>}
        </div>}

      {/* Load More */}
      {hasMore && <div className="text-center" data-id="element-20354">
          <button onClick={() => setVisibleCount(prev => prev + REPORTS_PER_PAGE)} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-20355">
            <ChevronDown className="w-3.5 h-3.5" data-id="element-20356" />
            {t('userdash.reports.showMore')} ({filtered.length - visibleCount})
          </button>
        </div>}

      {/* Stats Footer */}
      <div className="flex gap-3" data-id="element-20357">
        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-center" data-id="element-20358">
          <div className="text-lg font-bold text-slate-900" data-id="element-20359">{readyCount}</div>
          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20360">
            {t('userdash.reports.ready')}
          </div>
        </div>
        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-center" data-id="element-20361">
          <div className="text-lg font-bold text-slate-900" data-id="element-20362">{totalPages}</div>
          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20363">
            {t('userdash.reports.pages')}
          </div>
        </div>
        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-center" data-id="element-20364">
          <div className="text-lg font-bold text-amber-600" data-id="element-20365">
            {reports.filter(r => r.reviewed || submittedReviews.includes(r.id)).length}
            /{readyCount}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20366">
            {t('userdash.reports.rated')}
          </div>
        </div>
      </div>
    </div>;
}